import MinusButton from "components/MinusButton";
import PlusButton from "components/PlusButton";
import SelectHookFormSingle from "components/SelectHookFormSingle";
import TextInputLabelHookForm from "components/TextInputLabelHookForm";
import { Controller, useWatch } from "react-hook-form";
import { useState } from "react";
import { useDebounce } from "../hooks/useDebounce";
export const CardReaderAdmin = ({
  siteAdminFields,
  appendSiteAdmin,
  removeSiteAdmin,
  FormMethods,
  isMasterAdmin,
  siteAdminOptions,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const { register, control, watch, setValue, formState, setError, getValues } =
    FormMethods;
  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, 300);

  const watchedSiteAdmins = useWatch({
    control,
    name: `${isMasterAdmin ? "masterAdmin" : "siteAdmins"}`,
  });

  return (
    <div className="pb-6  mt-4 px-4 space-y-2 bg-white box-shadow rounded-lg">
      <div className="flex items-center justify-between pt-6 pb-2">
        <div className="text-blue-800 font-semibold text-sm">
          {isMasterAdmin ? "Master" : "Site"} Admin Details
        </div>
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-gray-600 hover:text-black"
        ></button>
      </div>

      {isExpanded &&
        siteAdminFields.map((siteAdminField, index) => {
          const siteAdminName = `${
            isMasterAdmin ? "masterAdmin" : "siteAdmins"
          }.${index}`;
          const selectedIds = watchedSiteAdmins
            ?.map((admin, idx) => (idx !== index ? admin.employeeId : null))
            .filter((id) => id !== null && id !== undefined);

          const selectedOption = siteAdminOptions.find(
            (option) => option.value === watch(`${siteAdminName}.employeeId`)
          );

          const othersOption = siteAdminOptions.find((o) => o.value === -1)!;

          const matchingOptions = siteAdminOptions
            .filter(
              (option) =>
                option.value !== -1 &&
                option.value !== selectedOption?.value &&
                !selectedIds.includes(option.value) &&
                option.label
                  .toLowerCase()
                  .includes(debouncedSearchText.toLowerCase())
            )
            .slice(0, 10);

          const filteredOptions = [
            othersOption,
            ...(selectedOption && selectedOption.value !== -1
              ? [selectedOption]
              : []),
            ...matchingOptions,
          ];

          const formStateErrors = formState.errors;
          const adminErrors =
            formStateErrors[
              `${isMasterAdmin ? "masterAdmin" : "siteAdmins"}`
            ] ?? [];
          return (
            <section
              key={siteAdminField.id}
              className="grid grid-cols-[auto,1fr,1fr,1fr,1fr] gap-4 py-2 items-start"
            >
              <div className="flex items-start pt-1 w-5">
                {index !== 0 ? (
                  <MinusButton
                    type="button"
                    onClick={() => removeSiteAdmin(index)}
                    aria-label={`Remove group ${index + 1}`}
                  />
                ) : (
                  <PlusButton
                    type="button"
                    onClick={() =>
                      appendSiteAdmin({
                        employeeId: 0,
                        name: "",
                        phoneNumber: "",
                        email: "",
                        isFieldsDisabled: true,
                        isMasterAdmin: isMasterAdmin,
                      })
                    }
                    className={
                      siteAdminFields.length >= (isMasterAdmin ? 5 : 10)
                        ? "opacity-50 cursor-not-allowed pointer-events-none"
                        : ""
                    }
                    aria-label="Add new group"
                  />
                )}
              </div>

              <div className="flex flex-col">
                <Controller
                  name={`${siteAdminName}.employeeId` as const}
                  control={control}
                  rules={{
                    required: "Employee is required",
                    validate: (value) => {
                      if (!value) {
                        return "Employee is required";
                      }
                      return true;
                    },
                  }}
                  render={({ field, fieldState }) => {
                    return (
                      <div>
                        <SelectHookFormSingle
                          className={
                            fieldState.error
                              ? "border border-red-500 focus:ring-red-500 col-span-2"
                              : "border border-slate-200 col-span-2"
                          }
                          value={field.value}
                          required={true}
                          onChange={async (e) => {
                            const newValue = e ? e.value : null;
                            field.onChange(newValue);

                            if (
                              !newValue ||
                              newValue === 0 ||
                              newValue === null ||
                              newValue === undefined
                            ) {
                              setError(`${siteAdminName}.employeeId`, {
                                type: "required",
                                message: "Employee is required",
                              });
                            }
                            setValue(
                              `${siteAdminName}.isFieldsDisabled`,
                              e == null ? true : e.id === 0 ? false : true
                            );
                            setValue(
                              `${siteAdminName}.name`,
                              e?.fullname ?? ""
                            );
                            setValue(`${siteAdminName}.email`, e?.email ?? "");
                            setValue(
                              `${siteAdminName}.phoneNumber`,
                              e?.phone ?? ""
                            );
                          }}
                          onInputChange={(input) => setSearchText(input)}
                          options={filteredOptions}
                          label={
                            isMasterAdmin
                              ? `Master Admin ${index + 1}`
                              : `Site Admin ${index + 1}`
                          }
                        />
                      </div>
                    );
                  }}
                />
              </div>

              <div className="flex flex-col">
                <TextInputLabelHookForm
                  className={
                    adminErrors[`${index}`]?.name
                      ? "border border-red-500 focus:ring-red-500 col-span-2"
                      : "border border-slate-200 col-span-2"
                  }
                  name={`${siteAdminName}.name`}
                  label="Name"
                  type="text"
                  maxLength={50}
                  register={register(`${siteAdminName}.name` as const, {
                    required:
                      getValues(`${siteAdminName}.employeeId`) === -1
                        ? "Name is required"
                        : false,
                    validate: (value) => {
                      const isOthers =
                        watch(`${siteAdminName}.employeeId`) === -1;
                      if (!isOthers) return true;

                      const duplicate = watchedSiteAdmins.some(
                        (admin, i) =>
                          i !== index &&
                          (admin.employeeId === 0 || admin.employeeId === -1) &&
                          admin.name === value
                      );
                      return duplicate ? "Duplicate name not allowed." : true;
                    },
                  })}
                  disabled={
                    (index === 0 && !watch(`${siteAdminName}.employeeId`)) ||
                    watch(`${siteAdminName}.isFieldsDisabled`)
                  }
                  required={watch(`${siteAdminName}.employeeId`) === -1}
                />
              </div>

              <div className="flex flex-col">
                <TextInputLabelHookForm
                  className={
                    adminErrors[`${index}`]?.email
                      ? "border border-red-500 focus:ring-red-500 col-span-2"
                      : "border border-slate-200 col-span-2"
                  }
                  name={`${siteAdminName}.email`}
                  label="Email"
                  maxLength={50}
                  register={register(`${siteAdminName}.email` as const, {
                    required:
                      watch(`${siteAdminName}.employeeId`) === -1
                        ? "Email is required"
                        : false,
                    pattern: {
                      value: /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i,

                      message: "Invalid email address",
                    },
                    validate: (value) => {
                      const isOthers =
                        watch(`${siteAdminName}.employeeId`) === -1;
                      if (!isOthers) return true;

                      // Duplicate checking is per input not row
                      const duplicate = watchedSiteAdmins.some(
                        (admin, i) =>
                          i !== index &&
                          (admin.employeeId === 0 || admin.employeeId === -1) &&
                          admin.email === value
                      );
                      return duplicate ? "Duplicate email not allowed." : true;
                    },
                  })}
                  disabled={
                    (index === 0 && !watch(`${siteAdminName}.employeeId`)) ||
                    watch(`${siteAdminName}.isFieldsDisabled`)
                  }
                  required={watch(`${siteAdminName}.employeeId`) === -1}
                />
              </div>

              <div className="flex flex-col">
                <TextInputLabelHookForm
                  className={
                    adminErrors[`${index}`]?.phoneNumber
                      ? "border border-red-500 focus:ring-red-500 col-span-2"
                      : "border border-slate-200 col-span-2"
                  }
                  name={`${siteAdminName}.phoneNumber`}
                  label="Phone Number"
                  type="text"
                  maxLength={50}
                  register={register(`${siteAdminName}.phoneNumber` as const, {
                    required:
                      watch(`${siteAdminName}.employeeId`) === -1
                        ? "Phone number is required"
                        : false,
                    validate: (value) => {
                      const isOthers =
                        watch(`${siteAdminName}.employeeId`) === -1;

                      if (!isOthers) return true;

                      const duplicate = watchedSiteAdmins.some(
                        (admin, i) =>
                          i !== index &&
                          (admin.employeeId === 0 || admin.employeeId === -1) &&
                          admin.phoneNumber === value
                      );
                      return duplicate
                        ? "Duplicate phone number not allowed."
                        : true;
                    },
                  })}
                  disabled={
                    (index === 0 && !watch(`${siteAdminName}.employeeId`)) ||
                    watch(`${siteAdminName}.isFieldsDisabled`)
                  }
                  required={watch(`${siteAdminName}.employeeId`) === -1}
                />
              </div>
            </section>
          );
        })}
    </div>
  );
};
